<script setup>
import { ref, shallowRef, reactive, computed, nextTick, onMounted, set, defineProps, defineExpose } from "vue";
import { debounce } from "lodash-es";
import FormItem from "@/components/basic/form/form-item/Index.vue";
import { useCsrCmpState } from "@/hooks/common/useCsrCmpState";
import { useGetDivision } from "@/hooks/form-v3/useIServiceDivisionCode";
import { useGetServiceLabel } from "@/hooks/form-v3/useIServiceLabel";
import {
  useGetAddressConfig,
  useGetParentRegionToCountry,
  useGetRegionDataSource,
  useGetZipCodeAddress,
} from "@/hooks/api/useIServiceApi";
import getSiteConfig from "./siteConfig";
import AddressInputByGoogle from "@/components/basic/address-input-by-google/Index.vue";

const site = document.body.dataset.site;
const { language } = document.body.dataset;
// 弹窗为 append-to-body，样式版本靠弹窗自身类名区分：body 带 page-v2 时用 v2 类名
const dialogThemeClass = document.body.classList.contains("page-v2") ? "address-form-modal-v2" : "address-form-modal";

const props = defineProps({
  stateKey: {
    type: String,
    default: "",
  },
  // 是否对 address1 启用 Google 地址联想，由上层组件显式传入
  searchAddressByGoogle: {
    type: Boolean,
    default: false,
  },
  // 是否渲染 state 字段 label 旁的帮助提示（helpText），文案取 label 配置表 Address_desc
  needStateLabelRender: {
    type: Boolean,
    default: true,
  },
  // 地址详情对象，由父组件传入用于弹窗回显（state.value 只存拼接后的完整地址字符串）
  addressDetail: {
    type: Object,
    default: null,
  },
});

// eslint-disable-next-line
const { state } = useCsrCmpState({
  stateKey: props.stateKey,
});

const emit = defineEmits(["change"]);

// 弹窗相关状态
const visible = ref(false);
const initError = ref(false);

const siteConfig = getSiteConfig(site);
const areaFields = ["state", "city", "district", "extra"];
const hideFields = ref(siteConfig.hideAddressFields || []);

// 从接口获取的数据
const labelConfig = ref({});
const levelConfig = ref({});
const addressConfig = ref(null);
const countryCodeList = ref([]);

// 使用 shallowRef：field.dataSource 需保持为真正的 ref（模板按 field.dataSource?.value 读取）
// 若用深 ref，嵌套 ref 会被 reactive 代理自动解包，导致 .value 赋值写到数组属性上而非 ref
const addressFormMap = shallowRef({});
const addressFormValue = reactive({
  fullAddress: "",
  state: "",
});

// country选项框相关
const countryCode = ref(null);
const countryFormMap = computed(() => ({
  label: labelConfig.value["PR_country/region"]?.label || labelConfig.value.ad_country?.label,
  show: !["vn", "hk"].includes(site),
  required: true,
  // 国家选项列表（用于显示名称）
  dataSource: countryCodeList.value.map((item) => ({
    label: item[language === "en" ? "name" : "name_local"] || item.name,
    value: item.code,
    ...item,
  })),
}));

const viewAddress = ref("");

// 弹窗内各字段的校验错误信息
// 用 ref 整体替换：Vue 2.7 的 reactive 侦测不到属性新增/删除，赋值后不会触发重渲染
const fieldErrors = ref({});

onMounted(() => {
  initData();
  // 展示地址的输入框不走组件 disabled（避免禁用态样式），改为禁用原生 input，防止 tab 键切换手动输入
  state.el?.querySelector("input")?.setAttribute("disabled", "disabled");
});

// 初始化：从接口获取 countryCode 和 labelConfig
const initData = async () => {
  try {
    const { countryCode: countryCodeJSON } = await useGetDivision();
    if (countryCodeJSON) {
      // 接口可能返回非法 JSON 字符串，单独捕获，避免中断后续 labelConfig 加载
      try {
        countryCodeList.value = typeof countryCodeJSON === "string" ? JSON.parse(countryCodeJSON) : countryCodeJSON;
      } catch {
        countryCodeList.value = [];
      }
    }
    labelConfig.value = await useGetServiceLabel() || {};

    // 当只有一个国家时自动选中
    if (countryFormMap.value.dataSource.length === 1) {
      countryCode.value = countryFormMap.value.dataSource[0].code;
      setDynamicFormMap();
    }
    // 回显初始值：优先取详情对象，表单未构建时退回 state.value（字符串）
    viewAddress.value = props.addressDetail?.fullAddress || state.value || "";
  } catch (e) {
    console.error("addressForm initData error:", e);
  }
};

// 设置动态表单
const setDynamicFormMap = async () => {
  const addressInterfaceLangMap = { hk: "85" };

  try {
    const { data } = await useGetAddressConfig({
      language: addressInterfaceLangMap[site],
      countryCode: countryCode.value,
    });
    addressConfig.value = data;

    const obj = {
      fullAddress: {
        itemType: "input",
        label:
          labelConfig.value.PR_street?.label ||
          labelConfig.value.Title_Fulladdress?.label,
        disabled: true,
      },
    };

    const formMap = getAddressFormMap(data.value?.itemList || []);
    addressFormMap.value = { ...formMap, ...obj };
    initValue();
    // formMap 异步构建完成后才能加载区域数据源（回显值已就位，无上级值的字段不发请求）
    initRegionDataSource(areaFields);
  } catch {
    initError.value = true;
  }
};

// 构建省市区联动 map 配置
const getRegionMap = (lvConfig = {}) => {
  const regionMap = {};

  // 清空下级区域字段的值与数据源
  const clearRegionFields = (fieldNames) => {
    fieldNames.forEach((name) => {
      if (addressFormMap.value[name]?.itemType === "select") {
        delete addressFormValue[name];
        if (regionMap[name]) {
          regionMap[name].dataSource.value = [];
        }
      }
    });
  };

  // 省
  regionMap.state = {
    itemType: "select",
    // state 字段 label 旁的帮助提示：由 needStateLabelRender 控制，文案取 label 配置表 Address_desc
    helpText: props.needStateLabelRender ? labelConfig.value.Address_desc?.label || "" : "",
    className: "address-form-select address-form-select-state",
    dataSource: ref([]),
    searchKeyword: ref(""),
    async requestFn() {
      if (!countryCode.value) {
        return [];
      }
      return getRegionDataSource({ parentCode: countryCode.value });
    },
    onChange: () => {
      nextTick(() => {
        clearRegionFields(["city", "district", "extra"]);
        ["city", "district", "extra"].forEach((i) => {
          if (hideAddressItem(i) && addressFormValue[i]) {
            delete addressFormValue[i];
          }
        });
        // 预加载市级数据源，下拉展开时即有数据
        initRegionDataSource(["city"]);
        resetDetailsAddress();
      });
    },
  };

  // 市
  regionMap.city = {
    itemType: "select",
    className: "address-form-select address-form-select-city",
    dataSource: ref([]),
    searchKeyword: ref(""),
    async requestFn(query = "") {
      if (query) {
        return getParentRegionToCountry({
          countryCode: countryCode.value,
          nameLoc: query,
          level: lvConfig.city,
        });
      }
      if (!addressFormValue.state) {
        return [];
      }
      return getRegionDataSource({ parentCode: JSON.parse(addressFormValue.state).code });
    },
    onChange: (val) => {
      nextTick(() => {
        if (val && JSON.parse(val) instanceof Array) {
          setAllAreasVal(val, lvConfig);
        } else {
          clearRegionFields(["district", "extra"]);
        }
        ["district", "extra"].forEach((i) => {
          if (hideAddressItem(i) && addressFormValue[i]) {
            delete addressFormValue[i];
          }
        });
        // 预加载下级数据源；无区级字段时街道直接依赖市级，需一并加载
        const nextFields = ["district"];
        if (addressFormValue.extra || addressFormMap.value.district?.itemType !== "select") {
          nextFields.push("extra");
        }
        initRegionDataSource(nextFields);
        resetDetailsAddress();
      });
    },
  };

  // 区
  regionMap.district = {
    itemType: "select",
    className: "address-form-select address-form-select-district",
    dataSource: ref([]),
    searchKeyword: ref(""),
    async requestFn(query = "") {
      if (query) {
        return getParentRegionToCountry({
          countryCode: countryCode.value,
          nameLoc: query,
          level: lvConfig.district,
        });
      }
      if (!addressFormValue.city) {
        return [];
      }
      return getRegionDataSource({ parentCode: JSON.parse(addressFormValue.city).code });
    },
    onChange: (val) => {
      nextTick(() => {
        if (val && JSON.parse(val) instanceof Array) {
          setAllAreasVal(val, lvConfig);
        } else {
          clearRegionFields(["extra"]);
        }
        // 预加载街道数据源
        initRegionDataSource(["extra"]);
        resetDetailsAddress();
      });
    },
  };

  // 街道
  regionMap.extra = {
    itemType: "select",
    className: "address-form-select address-form-select-extra",
    dataSource: ref([]),
    searchKeyword: ref(""),
    async requestFn(query = "") {
      if (query) {
        return getParentRegionToCountry({
          countryCode: countryCode.value,
          nameLoc: query,
          level: lvConfig.extra,
        });
      }
      if (!addressFormValue.city) {
        return [];
      }
      return getRegionDataSource({ parentCode: JSON.parse(addressFormValue.city).code });
    },
    onChange: (val) => {
      nextTick(() => {
        if (val && JSON.parse(val) instanceof Array) {
          setAllAreasVal(val, lvConfig);
        }
        resetDetailsAddress();
      });
    },
  };

  // 详细地址
  regionMap.address1 = {
    // 开启 Google 地址联想时使用自定义输入组件，否则为普通输入框
    itemType: props.searchAddressByGoogle ? "address-input-by-google" : "input",
    onChange: () => {
      nextTick(() => {
        resetDetailsAddress();
      });
    },
  };

  return regionMap;
};

// 获取输入框字段配置
const getInputConfig = (fieldName = "") => {
  return {
    itemType: "input",
    // MdInput 的 input/change 事件均直接抛出字符串值
    onInput: (val) => {
      nextTick(() => {
        if (fieldName === "zipcode") {
          getZipCodeAddressDebounce(val);
        }
        resetDetailsAddress();
      });
    },
    onChange: (val) => {
      nextTick(() => {
        if (fieldName === "zipcode" && !val) {
          handleZipCodeChange({ empty: true });
        }
        resetDetailsAddress();
      });
    },
  };
};

// 获取动态表单配置
const getAddressFormMap = (configList) => {
  const formMap = {};
  levelConfig.value = configList.reduce((obj, item) => {
    const { fieldName, regionLevel } = item;
    if (areaFields.includes(fieldName)) {
      obj[fieldName] = regionLevel.toString();
    }
    return obj;
  }, {});

  const regionMap = getRegionMap(levelConfig.value);

  // Vue 2.7 的 reactive 基于 defineProperty，无法侦测对象新增属性。
  // 字段名由后端 itemList 动态驱动，需在渲染前用 set 预注册为响应式，
  // 否则后续 addressFormValue[fieldName] 赋值不会触发模板 :value 更新，
  // 受控输入被 setNativeInputValue 重置，表现为输入只剩一个字符。
  configList.forEach(({ fieldName }) => {
    if (!hideAddressItem(fieldName) && !(fieldName in addressFormValue)) {
      set(addressFormValue, fieldName, "");
    }
  });

  configList.forEach((item) => {
    const { fieldName, type, requiredFlag, displayname = "" } = item;
    if (hideAddressItem(fieldName)) {
      return;
    }

    if (["Input", "BaseAddress"].includes(type) && fieldName !== "address1") {
      formMap[fieldName] = getInputConfig(fieldName);
    } else {
      formMap[fieldName] = regionMap[fieldName];
    }

    formMap[fieldName].label =
      (labelConfig.value[fieldName] || {}).label || displayname || fieldName;

    if (requiredFlag === "Y") {
      formMap[fieldName].required = true;
    }
  });

  return formMap;
};

// 已确认的表单值快照，取消时用于回滚未确认的编辑
let formValueSnapshot = {};

const takeFormSnapshot = () => {
  formValueSnapshot = { ...addressFormValue };
};

const restoreFormSnapshot = () => {
  if (!Object.keys(formValueSnapshot).length) {
    return;
  }
  Object.keys(addressFormValue).forEach((key) => {
    if (key in formValueSnapshot) {
      addressFormValue[key] = formValueSnapshot[key];
    } else {
      delete addressFormValue[key];
    }
  });
};

// 初始化值（有回显值则回填，否则清空表单）
const initValue = () => {
  // 回显数据来自父组件传入的地址详情对象
  const detail = props.addressDetail;
  if (!detail || !detail.fullAddress) {
    // 清空除 fullAddress 外的所有字段
    Object.keys(addressFormValue).forEach((key) => {
      if (key !== "fullAddress") {
        addressFormValue[key] = "";
      }
    });
    takeFormSnapshot();
    return;
  }
  Object.keys(detail).forEach((key) => {
    addressFormValue[key] = detail[key];
  });

  // 区域字段回显值转为 select 所需的 JSON 字符串
  areaFields.forEach((f) => {
    if (detail[f]) {
      addressFormValue[f] = JSON.stringify(detail[f]);
    }
  });

  resetDetailsAddress();
  takeFormSnapshot();
  nextTick(() => {
    viewAddress.value = addressFormValue.fullAddress;
    emit("change", {
      ...detail,
      countryCode: countryCode.value,
      fullAddress: viewAddress.value,
    });
  });
};

// 判断字段是否隐藏
const hideAddressItem = (fieldName) => {
  return hideFields.value.includes(fieldName);
};

// 获取省市区县的 label 和 value
const getRegionItemLabelValue = (val = {}) => {
  const { nameEnu, nameLoc, code } = val;
  const name = language === "en" ? nameEnu : nameLoc;
  return {
    label: name,
    value: JSON.stringify({ code, nameLoc: name }),
  };
};

// 获取区域数据源
const getRegionDataSource = async ({ parentCode }) => {
  const { data } = await useGetRegionDataSource({ parentCode });
  // useFetch 返回的 data 是 ref，需取 .value
  return (data.value || []).map((item) => getRegionItemLabelValue(item));
};

// 模糊查询上层行政区划
const getParentRegionToCountry = async ({ countryCode, nameLoc, level }) => {
  const { data } = await useGetParentRegionToCountry({
    countryCode,
    nameLoc,
    level,
  });
  const dataSource = [];
  const labelKey = language === "en" ? "nameEnu" : "nameLoc";
  // useFetch 返回的 data 是 ref，需取 .value
  Object.keys(data.value || {}).forEach((key) => {
    const label = data.value[key].reduce((str, item) => {
      str && (str = str + " - ");
      return str + item[labelKey];
    }, "");
    dataSource.push({
      label,
      value: JSON.stringify(data.value[key]),
    });
  });
  return dataSource;
};

// 批量加载区域字段数据源（仅 select 类型；无上级值的字段 requestFn 返回空，不发请求）
const initRegionDataSource = (fieldNames) => {
  fieldNames.forEach((fieldName) => {
    const field = addressFormMap.value[fieldName];
    if (field?.itemType !== "select" || !field.requestFn) {
      return;
    }
    field.requestFn().then((data) => {
      field.dataSource.value = data;
    });
  });
};

// 清空区域下拉字段的数据源（弹窗关闭时调用，避免残留旧国家的选项）
const clearRegionDataSource = () => {
  areaFields.forEach((name) => {
    const field = addressFormMap.value[name];
    if (field?.itemType === "select" && field.dataSource) {
      field.dataSource.value = [];
    }
  });
};

// 国家变更（v-model 已在 change 前更新 countryCode，此处无需重复赋值）
const handleCountryChange = () => {
  // 整体替换以触发响应式更新，置空即代表无错误
  fieldErrors.value = { ...fieldErrors.value, countryCode: "" };
  nextTick(() => {
    setDynamicFormMap();
  });
};

// 字段事件统一处理：写入表单值 → 清除该字段错误 → 执行字段自定义钩子（钩子内部会重算 fullAddress）→ 无钩子时兜底重算
const createFieldHandler = (hookName) => (fieldName, val) => {
  addressFormValue[fieldName] = val;
  fieldErrors.value = { ...fieldErrors.value, [fieldName]: "" };
  const hook = addressFormMap.value[fieldName]?.[hookName];
  if (hook) {
    hook(val);
  } else {
    resetDetailsAddress();
  }
};

// 字段值变更
const handleFieldChange = createFieldHandler("onChange");

// 字段输入事件（MdInput 的 input 事件直接抛出字符串值，非 DOM 事件）
const handleFieldInput = createFieldHandler("onInput");

// 解析区域字段显示名称
const resolveFieldName = (fieldName, rawVal) => {
  if (!rawVal) {
    return "";
  }
  if (addressFormMap.value[fieldName]?.itemType === "input") {
    return rawVal;
  }
  try {
    return JSON.parse(rawVal).nameLoc || "";
  } catch {
    return "";
  }
};

// 拼接详细地址
const resetDetailsAddress = () => {
  const {
    zipcode = "",
    address1 = "",
    address2 = "",
    address3 = "",
    state: stateVal,
    city: cityVal,
    district: districtVal,
    extra: extraVal,
  } = addressFormValue;

  const extraName = resolveFieldName("extra", extraVal);
  const districtName = resolveFieldName("district", districtVal);
  const cityName = resolveFieldName("city", cityVal);
  const stateName = resolveFieldName("state", stateVal);

  let countryName = "";
  const matched = countryFormMap.value.dataSource.find((item) => item.value === countryCode.value);
  if (matched) {
    countryName = matched[language === "en" ? "name" : "name_local"] || matched.name;
  }

  const val = {
    zipcode,
    address1,
    address2,
    address3,
    state: stateName,
    city: cityName,
    district: districtName,
    extra: extraName,
    country: countryName,
  };

  const { assemblyRule = "" } = addressConfig.value || {};
  const addressOrderArr = assemblyRule.match(/\[(.+?)\]/g);
  const seperateSymble = assemblyRule.match(/\](.+?)\[/g);
  const squareReg = /\[\s*|\s*\]/g;

  const addressDetailArr = [];
  if (addressOrderArr && addressOrderArr.length) {
    addressOrderArr.forEach((item, index) => {
      const tempitem = item.replace(squareReg, "");
      let temsep = index === addressOrderArr.length - 1 ? "" : seperateSymble[index];
      temsep = temsep.replace(squareReg, "");
      if (!hideAddressItem(tempitem) && val[tempitem]) {
        addressDetailArr.push(val[tempitem]);
        addressDetailArr.push(temsep);
      }
    });
  }
  addressDetailArr.pop();

  addressFormValue.fullAddress = addressDetailArr.join("").trim();
};

// 设置省市区县的值（支持多级联动选择）
const setAllAreasVal = (val, lvConfig) => {
  if (typeof val === "string") {
    val = JSON.parse(val);
  }
  val.reverse().forEach((item) => {
    Object.keys(lvConfig).forEach((fieldName) => {
      // eslint-disable-next-line
      if (lvConfig[fieldName] == item.level) {
        if (fieldName === "state") {
          handleZipCodeChange({ empty: false });
        }
        const { label, value } = getRegionItemLabelValue(item);
        if (addressFormMap.value[fieldName]?.itemType === "select") {
          addressFormValue[fieldName] = value;
        } else {
          addressFormValue[fieldName] = label;
        }
      }
    });
  });
  resetDetailsAddress();
};

// 根据 zipcode 反查地址
const getZipCodeAddress = async (zipcode) => {
  if (!zipcode) {
    handleZipCodeChange({ empty: true });
    return;
  }
  try {
    const { data } = await useGetZipCodeAddress({
      zipcode,
      countryCode: countryCode.value,
    });
    // useFetch 返回的 data 是 ref，需取 .value
    setAllAreasVal(data.value || [], levelConfig.value);
    // 反查回填后上级值已就位，重新加载数据源，使 select 能按 value 匹配到 option 反显 label
    // 实际请求的字段由 addressConfig 生成的 addressFormMap 决定（initRegionDataSource 内部过滤未配置/非 select 字段）
    initRegionDataSource(areaFields);
    if (!data.value || !data.value.length) {
      handleZipCodeChange({ empty: true });
    }
  } catch {
    handleZipCodeChange({ empty: true });
  }
};

// debounce 包装
const getZipCodeAddressDebounce = debounce(getZipCodeAddress, 500);

// zipcode 与 state 联动
const handleZipCodeChange = ({ empty }) => {
  if (addressFormMap.value.state) {
    addressFormMap.value.state.disabled = !empty;
    // shallowRef 不追踪嵌套属性修改，整体替换以触发视图更新
    addressFormMap.value = { ...addressFormMap.value };
  }
};

// 校验所有字段
const validateAll = () => {
  const errors = {};
  let isValid = true;

  // 校验国家
  if (countryFormMap.value.show && !countryCode.value) {
    // 必填错误提示：与外层表单逻辑一致，取label配置表对应字段的validateTips.invalid，无配置时兜底默认文案
    errors.countryCode =
      labelConfig.value["PR_country/region"]?.validateTips?.invalid ||
      labelConfig.value.ad_country?.validateTips?.invalid || " ";
    isValid = false;
  }

  // 校验地址表单字段
  Object.keys(addressFormMap.value).forEach((key) => {
    const field = addressFormMap.value[key];
    if (field.required && !addressFormValue[key]) {
      // 必填错误提示：与外层表单逻辑一致，取label配置表对应字段的validateTips.invalid，无配置时兜底默认文案
      errors[key] = labelConfig.value[key]?.validateTips?.invalid || " ";
      isValid = false;
    }
  });

  fieldErrors.value = errors;
  return isValid;
};

// 打开弹窗
const handleOpen = async () => {
  visible.value = true;
  // 仅在初始化失败或未加载到配置时重试，避免与 initData 中的加载重复执行
  if (initError.value || !addressConfig.value) {
    await setDynamicFormMap();
  } else {
    // 关闭时已清空数据源，重新打开时按当前值预加载（无上级值的字段不发请求）
    initRegionDataSource(areaFields);
  }
};

// 确认
const handleOk = () => {
  if (!validateAll()) {
    return;
  }
  if (!addressFormValue.fullAddress) {
    return;
  }

  const addressObj = {};
  Object.keys(addressFormValue).forEach((key) => {
    if (areaFields.includes(key) && addressFormMap.value[key]?.itemType === "select") {
      addressObj[key] = addressFormValue[key]
        ? JSON.parse(addressFormValue[key])
        : "";
      return;
    }
    if (hideAddressItem(key) && addressFormValue[key]) {
      try {
        addressObj[key] = JSON.parse(addressFormValue[key]);
      } catch {
        addressObj[key] = addressFormValue[key];
      }
      return;
    }
    addressObj[key] = addressFormValue[key];
  });

  viewAddress.value = addressFormValue.fullAddress;
  // 弹窗确认后已有地址值，清除外层字段的必填错误状态
  state.conf.error = false;

  emit("change", { ...addressObj, countryCode: countryCode.value });
  visible.value = false;
  // 确认后的值成为新的回滚基线
  takeFormSnapshot();
};

// 取消（MdDialog 的 @close 也走此处，确认关闭同样会触发）
const handleCancel = () => {
  visible.value = false;
  // 回滚本次未确认的编辑及校验错误
  restoreFormSnapshot();
  fieldErrors.value = {};
  // 弹窗关闭时清空区域下拉数据源
  clearRegionDataSource();
};

// 暴露校验方法：resolve/reject 均携带 state，供 form-v3 收集字段值与展示错误
const validator = () => {
  if (viewAddress.value) {
    state.conf.error = false;
    return Promise.resolve(state);
  }
  // 仅标记错误状态，错误文案由 FormItem 兜底显示 conf.defErrorMsg
  state.conf.error = true;
  return Promise.reject(state);
};

defineExpose({
  validator,
});
</script>

<template>
  <div class="address-form">
    <FormItem v-bind="state.conf">
      <!-- 展示地址：disabled 在 onMounted 中设置到原生 input，避免组件禁用态样式 -->
      <MdInput
        :value="viewAddress"
        :placeholder="state.conf.placeholder || ''"
      />
      <div
        class="address-form-content-button"
        @click="handleOpen()"
      />
    </FormItem>

    <MdDialog
      :title="labelConfig.Title_address?.label || 'Address'"
      :visible="visible"
      :class="dialogThemeClass"
      append-to-body
      :close-on-click-modal="false"
      @close="handleCancel"
    >
      <div :class="`${dialogThemeClass}-body`">
        <!-- 国家选择 -->
        <FormItem
          v-if="countryFormMap.show"
          :label="countryFormMap.label"
          :required="countryFormMap.required"
          :error="!!fieldErrors.countryCode"
          :error-msg="fieldErrors.countryCode"
        >
          <md-select
            v-model="countryCode"
            filterable
            placeholder=""
            :popper-append-to-body="false"
            @change="handleCountryChange"
          >
            <md-option
              v-for="item in countryFormMap.dataSource"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </md-select>
        </FormItem>

        <!-- 动态地址表单字段 -->
        <template v-for="(field, fieldName) in addressFormMap">
          <!-- 输入框类型 -->
          <FormItem
            v-if="field.itemType === 'input'"
            :key="fieldName + '-input'"
            :label="field.label"
            :required="field.required"
            :error="!!fieldErrors[fieldName]"
            :error-msg="fieldErrors[fieldName]"
          >
            <MdInput
              :value="addressFormValue[fieldName]"
              :disabled="field.disabled"
              :maxlength="field.maxLength"
              placeholder=""
              @input="handleFieldInput(fieldName, $event)"
              @change="handleFieldChange(fieldName, $event)"
            />
          </FormItem>

          <!-- 下拉选择类型 -->
          <FormItem
            v-else-if="field.itemType === 'select'"
            :key="fieldName + '-select'"
            :label="field.label"
            :required="field.required"
            :help-text="field.helpText"
            :error="!!fieldErrors[fieldName]"
            :error-msg="fieldErrors[fieldName]"
          >
            <md-select
              :value="addressFormValue[fieldName]"
              filterable
              :placeholder="''"
              :popper-append-to-body="false"
              :class="field.className"
              @change="handleFieldChange(fieldName, $event)"
            >
              <md-option
                v-for="opt in field.dataSource?.value || []"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
              />
            </md-select>
          </FormItem>

          <!-- Google 地址联想输入类型 -->
          <FormItem
            v-else-if="field.itemType === 'address-input-by-google'"
            :key="fieldName + '-google'"
            :label="field.label"
            :required="field.required"
            :error="!!fieldErrors[fieldName]"
            :error-msg="fieldErrors[fieldName]"
          >
            <AddressInputByGoogle
              :value="addressFormValue[fieldName]"
              :disabled="field.disabled"
              @change="handleFieldChange(fieldName, $event)"
            />
          </FormItem>
        </template>
      </div>

      <template #footer>
        <div :class="`${dialogThemeClass}-footer`">
          <md-button
            :class="['md-button--default', `${dialogThemeClass}-cancel`]"
            @click="handleCancel"
          >
            {{ labelConfig.BTN_cancel?.label || "Cancel" }}
          </md-button>
          <md-button
            :class="['md-button--primary', `${dialogThemeClass}-confirm`]"
            @click="handleOk"
          >
            {{ labelConfig.common_button_ok?.label || labelConfig.BTN_ok?.label || "OK" }}
          </md-button>
        </div>
      </template>
    </MdDialog>
  </div>
</template>
